import React from "react";
import PropTypes from "prop-types";
import { Modal, Button } from "@weezevent/nacre";
import { useTranslation } from "react-i18next";
import ChecklistsTable from "../checklists-table/checklists-table";
import { ActionsTypes } from "../../constants";
import useCheckpoints from "../../../../../../pages/attendee-details/hooks/useCheckpoints";
import classes from "./access-rights-modal.module.scss";

export const AccessRightsModal = ({
	actionType,
	attendeeIds,
	onSubmit,
	onCancel,
	isSubmitting,
	eventId
}) => {
	const { t } = useTranslation();
	const [filters, setFilters] = React.useState({
		search: "",
		sorting: null,
		selectedCheckpoints: []
	});

	const { data: checkpointsData } = useCheckpoints({
		search: filters.search,
		queryParams: {
			archived: false
		},
		eventId
	});
	const checkpoints = checkpointsData?.data || [];

	const isAddRights = actionType === ActionsTypes.ADD_RIGHTS;
	const attendeeCount = attendeeIds?.length || 0;

	const title = React.useMemo(() => {
		if (isAddRights) {
			return t(
				"access.attendees.attendeesList.batchActions.addRights.modal.title",
				{
					count: attendeeCount
				}
			);
		}
		return t(
			"access.attendees.attendeesList.batchActions.removeRights.modal.title",
			{
				count: attendeeCount
			}
		);
	}, [isAddRights, attendeeCount, t]);

	const description = React.useMemo(() => {
		if (isAddRights) {
			return t(
				"access.attendees.attendeesList.batchActions.addRights.modal.description"
			);
		}
		return t(
			"access.attendees.attendeesList.batchActions.removeRights.modal.description"
		);
	}, [isAddRights, t]);

	const handleSubmit = () => {
		const selectedCheckpointObjects = (checkpoints || [])
			.filter(checkpoint =>
				filters.selectedCheckpoints.includes(checkpoint.id)
			)
			.map(checkpoint => ({
				id: checkpoint.id,
				default_nb_access: checkpoint.default_nb_access,
				strict_nb_access: checkpoint.strict_nb_access,
				is_using_strict_mode: checkpoint.is_using_strict_mode
			}));
		onSubmit(selectedCheckpointObjects);
	};

	const handleCancel = () => {
		setFilters({
			search: "",
			sorting: null,
			selectedCheckpoints: []
		});
		onCancel();
	};

	return (
		<Modal
			onClose={handleCancel}
			title={title}
			subtitle={description}
			className={classes.root}
			size="extralarge"
			cancelButton={
				<Button disabled={isSubmitting} onClick={handleCancel}>
					{t("access.common.cancel")}
				</Button>
			}
			submitButton={
				<Button
					disabled={
						isSubmitting || filters.selectedCheckpoints.length === 0
					}
					onClick={handleSubmit}
					primary
					className={
						isAddRights
							? classes["submit-button--add"]
							: classes["submit-button--remove"]
					}
				>
					{isAddRights
						? t(
								"access.attendees.attendeesList.batchActions.addRights.modal.submitButton"
						  )
						: t(
								"access.attendees.attendeesList.batchActions.removeRights.modal.submitButton"
						  )}
				</Button>
			}
		>
			<ChecklistsTable
				filters={filters}
				setFilters={setFilters}
				eventId={eventId}
				disabled={isSubmitting}
			/>
		</Modal>
	);
};

AccessRightsModal.propTypes = {
	actionType: PropTypes.string.isRequired,
	attendeeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
	onSubmit: PropTypes.func,
	onCancel: PropTypes.func,
	isSubmitting: PropTypes.bool,
	eventId: PropTypes.number
};

AccessRightsModal.defaultProps = {
	onSubmit: () => {},
	onCancel: () => {},
	isSubmitting: false,
	eventId: null
};
